import { useEffect, useRef, useState } from 'react'
// @ts-ignore
import * as THREE from 'three'
// @ts-ignore
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { useAppStore } from '@store/appStore'

interface SceneData {
  scene: THREE.Scene
  camera: THREE.PerspectiveCamera
  renderer: THREE.WebGLRenderer
  animationId: number
  cleanup?: () => void
}

/**
 * MeshViewer - Display COLMAP mesh files (GLB format)
 * Loads mesh from public/meshes/{videoId}/{sectionId}/mesh.glb
 * Falls back to public/meshes/{videoId}/mesh.glb for single-section reconstructions
 */
export function MeshViewer() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sceneRef = useRef<SceneData | null>(null)
  const [meshLoaded, setMeshLoaded] = useState(false)
  const [meshError, setMeshError] = useState<string | null>(null)
  const [totalSections, setTotalSections] = useState(0)
  
  const currentVideoId = useAppStore((state) => state.currentDatasetId)
  const currentMeshSection = useAppStore((state) => state.currentMeshSection)
  const setCurrentMeshSection = useAppStore((state) => state.setCurrentMeshSection)
  const isLoadingData = useAppStore((state) => state.isLoadingData)

  // Load mesh based on current video and section
  useEffect(() => {
    if (!canvasRef.current || !currentVideoId || isLoadingData) return

    let mounted = true
    let animationId = 0

    async function initScene() {
      try {
        // Scene setup
        const scene = new THREE.Scene()
        scene.background = new THREE.Color(0x2a2a2a)

        const camera = new THREE.PerspectiveCamera(
          75,
          canvasRef.current!.clientWidth / canvasRef.current!.clientHeight,
          0.1,
          10000
        )
        camera.position.z = 30

        const renderer = new THREE.WebGLRenderer({ 
          canvas: canvasRef.current!,
          antialias: true,
          alpha: true 
        })
        renderer.setSize(canvasRef.current!.clientWidth, canvasRef.current!.clientHeight)
        renderer.setPixelRatio(window.devicePixelRatio)

        // Lighting
        const light = new THREE.DirectionalLight(0xffffff, 1)
        light.position.set(5, 10, 7)
        scene.add(light)
        scene.add(new THREE.AmbientLight(0xffffff, 0.5))

        // Try to load mesh with section support
        let meshPath = `/meshes/${currentVideoId}/section_${currentMeshSection}/mesh.glb`
        let response = await fetch(meshPath)
        
        // Fallback to single-section path if section-specific doesn't exist
        if (!response.ok) {
          meshPath = `/meshes/${currentVideoId}/mesh.glb`
          response = await fetch(meshPath)
        }
        
        if (!response.ok) {
          throw new Error(`Mesh not found: tried section and single paths`)
        }

        const arrayBuffer = await response.arrayBuffer()
        
        // Load GLB
        const loader = new GLTFLoader()

        return new Promise<void>((resolve, reject) => {
          loader.parse(arrayBuffer, '', 
            (gltf: any) => {
              if (!mounted) {
                resolve()
                return
              }

              scene.add(gltf.scene)

              // Fit camera to mesh
              const box = new THREE.Box3().setFromObject(gltf.scene)
              const size = box.getSize(new THREE.Vector3())
              const center = box.getCenter(new THREE.Vector3())
              const maxDim = Math.max(size.x, size.y, size.z)
              const fov = camera.fov * (Math.PI / 180)
              const distance = maxDim / 2 / Math.tan(fov / 2) * 1.5

              camera.position.copy(center)
              camera.position.z += distance
              camera.lookAt(center)

              setMeshLoaded(true)

              // Animation loop
              const animate = () => {
                animationId = requestAnimationFrame(animate)
                renderer.render(scene, camera)
              }
              animate()

              sceneRef.current = { scene, camera, renderer, animationId }

              // Handle window resize
              const handleResize = () => {
                const width = canvasRef.current!.clientWidth
                const height = canvasRef.current!.clientHeight
                camera.aspect = width / height
                camera.updateProjectionMatrix()
                renderer.setSize(width, height)
              }

              window.addEventListener('resize', handleResize)

              sceneRef.current.cleanup = () => {
                window.removeEventListener('resize', handleResize)
              }

              resolve()
            },
            (err: any) => {
              if (mounted) {
                setMeshError(`Failed to parse mesh: ${err.message}`)
              }
              reject(err)
            }
          )
        })
      } catch (error) {
        if (mounted) {
          setMeshError(error instanceof Error ? error.message : 'Unknown error loading mesh')
          console.error('Mesh loading error:', error)
        }
      }
    }

    initScene()

    return () => {
      mounted = false
      if (sceneRef.current?.cleanup) {
        sceneRef.current.cleanup()
      }
      if (sceneRef.current?.animationId) {
        cancelAnimationFrame(sceneRef.current.animationId)
      }
      if (sceneRef.current?.renderer) {
        sceneRef.current.renderer.dispose()
      }
    }
  }, [currentVideoId, currentMeshSection, isLoadingData])

  // Detect number of available sections and set controls
  useEffect(() => {
    async function checkAvailableSections() {
      if (!currentVideoId) return
      
      let sections = 0
      for (let i = 0; i < 20; i++) {
        const response = await fetch(`/meshes/${currentVideoId}/section_${i}/mesh.glb`, { method: 'HEAD' })
        if (!response.ok) break
        sections = i + 1
      }
      
      if (sections === 0) {
        // Try single-section format
        const response = await fetch(`/meshes/${currentVideoId}/mesh.glb`, { method: 'HEAD' })
        if (response.ok) {
          sections = 1
        }
      }
      
      setTotalSections(sections)
    }
    
    checkAvailableSections()
  }, [currentVideoId])

  if (meshError) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#1a1a1a',
        color: '#f0f0f0',
        fontSize: '14px',
        flexDirection: 'column',
        gap: '10px'
      }}>
        <p>⚠️ Mesh Error</p>
        <p style={{ fontSize: '12px', color: '#999' }}>{meshError}</p>
      </div>
    )
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <canvas
        ref={canvasRef}
        style={{
          flex: 1,
          display: 'block',
          background: meshLoaded ? 'auto' : '#1a1a1a'
        }}
      />
      
      {/* Section Navigation */}
      {totalSections > 1 && (
        <div style={{
          background: '#1a1a1a',
          color: '#f0f0f0',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '12px',
          borderTop: '1px solid #333'
        }}>
          <button
            onClick={() => setCurrentMeshSection(Math.max(0, currentMeshSection - 1))}
            disabled={currentMeshSection === 0}
            style={{
              padding: '4px 8px',
              background: currentMeshSection === 0 ? '#444' : '#666',
              color: '#fff',
              border: 'none',
              cursor: currentMeshSection === 0 ? 'default' : 'pointer',
              borderRadius: '3px'
            }}
          >
            ← Prev
          </button>
          
          <span style={{ flex: 1 }}>
            Section {currentMeshSection} / {totalSections - 1}
          </span>
          
          <button
            onClick={() => setCurrentMeshSection(Math.min(totalSections - 1, currentMeshSection + 1))}
            disabled={currentMeshSection === totalSections - 1}
            style={{
              padding: '4px 8px',
              background: currentMeshSection === totalSections - 1 ? '#444' : '#666',
              color: '#fff',
              border: 'none',
              cursor: currentMeshSection === totalSections - 1 ? 'default' : 'pointer',
              borderRadius: '3px'
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  )
}
